import { Injectable } from '@angular/core';
import { KalDialogConfig, KalDialogService, KalSnackbarConfig, KalSnackbarService } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import { BehaviorSubject, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import {
    generateEnvoieMailPublipostageXML,
    generateModeleEMailPublipostageXML,
    generateSendMailWithCourrierXML
} from 'src/app/_core/services/mail.xml';
import { GeneratedMailDocument, SharedMail } from 'src/app/_shared/components/mail/shared-mail/shared-mail.component';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';
import {
    SharedMailNonContactableBeneficiariesDialogComponent,
    SharedMailNonContactableBeneficiariesDialogData
} from 'src/app/_shared/dialogs/mail/shared-mail-non-contactable-beneficiaries-dialog/shared-mail-non-contactable-beneficiaries-dialog.component';
import {
    SharedMailPreviewDialogComponent,
    SharedMailPreviewDialogData
} from 'src/app/_shared/dialogs/shared-mail-preview-dialog/shared-mail-preview-dialog.component';
import { UserState } from 'src/app/_user/store/user/user.state';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import {
    BeneficiaireAvecMailsModel,
    FichierCompletModel,
    FichierModelInput,
    MailModelDtoInput,
    ModelesModelEmailPieceJointeModel,
    ModelesModelEmailPieceJointeModelInput,
    MutationEnvoieMailPublipostageArgs,
    MutationEnvoieMailUtilisateurPublipostageArgs,
    MutationGetBenefNonContactableArgs,
    MutationGetModeleEMailPublipostageArgs,
    MutationGetValueParametrePublipostageLimiteAvertissementArgs,
    MutationGetValueParametrePublipostageLimiteBlocageArgs,
    MutationSendMailWithCourrierArgs
} from 'src/graphql/generated-types';

export interface MailCeToBenef {
    label?: string;
    parameters: { [key: string]: any };
    reportCode: string;
    benefId: number;
    mailCC: boolean;
}

export interface BeneficiaryAddress {
    IsEmailPro: boolean;
    IsEmailPerso: boolean;
    IsEmailProfOrPerso: boolean;
    IsEmailPriviligie: boolean;
    IsEmailPriviligiePerso: boolean;
    IsEmailPriviligiePro: boolean;
}

/**
 * Type avec les champs pour l'addresse email destinataire du type MailModelDtoInput
 */
export type MailModelDTORecipient = Pick<MailModelDtoInput, 'AdrPrivilegierPerso'| 'AdrPrivilegierPro'|'AdresseLesDeux'|'AdressePerso'|'AdressePrivilegier'|'AdressePro'>;

export type MailContext = 'mail' | 'letter' | 'newsletter';

export type PublipostageMail = {
    mail: SharedMail,
    beneficiaryId: number,
    idTypePubli: number
};

@Injectable({
    providedIn: 'root'
})
export class MailService {

    private blockingLimit: number = null;

    private warningLimit: number = null;

    private _loading = new BehaviorSubject(false);

    constructor(private readonly dialogService: KalDialogService,
                private readonly graphqlService: GraphqlService,
                private readonly snackbarService: KalSnackbarService,
                private readonly store: Store) {
    }

    get loading(): boolean {
        return this._loading.getValue();
    }

    set loading(loading: boolean) {
        this._loading.next(loading);
    }

    get loading$(): Observable<boolean> {
        return this._loading.asObservable();
    }

    sendMail({mail, mailParameters}: { mail: SharedMail, mailParameters: MailCeToBenef }): Observable<any> {
        this.loading = true;

        return this.graphqlService.mutateOne<'SendMailWithCourrier', MutationSendMailWithCourrierArgs>({
            mutation: gql`
                mutation SendMailWithCourrier(
                    $contexte: ContextInfosInput,
                    $rawXML: String
                ) {
                    SendMailWithCourrier(
                        contexte: $contexte,
                        rawXML: $rawXML
                    )
                }
            `,
            variables: {
                rawXML: this.getMail({mail, mailParameters})
            }
        }).pipe(
            tap(() => {
                this.snackbarService.open(new KalSnackbarConfig({title: 'L\'e-Mail a bien été envoyé.'}));
                this.loading = false;
            }),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    sendPublipostageMail({mail, idsList, context, generatedDocumentsList}: {
        mail: SharedMail,
        idsList: number[],
        context?: MailContext,
        generatedDocumentsList?: GeneratedMailDocument[]
    }): Observable<any> {

        this.loading = true;

        return this.getPublipostageMailsLimits().pipe(
            tap(() => this.loading = false),
            switchMap(() => this.checkPublipostageMailsLimits({mail, idsList})),
            tap(() => this.loading = true),
            // Génération des documents sélectionnés, joints au mail
            switchMap(() => this.generateSelectedDocuments$(mail, generatedDocumentsList)),
            map(generatedVouchersList => ({
                ...mail,
                vouchersList: [...(mail.vouchersList || []), ...generatedVouchersList]
            })),
            switchMap(mailToSend => this.graphqlService.mutateOne<'EnvoieMailPublipostage', MutationEnvoieMailPublipostageArgs>({
                mutation: gql`
                mutation EnvoieMailPublipostage(
                    $contexte: ContextInfosInput,
                    $rawXML: String
                ) {
                    EnvoieMailPublipostage(
                        contexte: $contexte,
                        rawXML: $rawXML
                    ) {
                         NombreContacte
                         NomUniqueContacte
                         NombreNonContacte
                         NomUniqueNonContacte
                     }
                }`,
                variables: {
                    rawXML: generateEnvoieMailPublipostageXML(this.getPublipostageMail(mailToSend), this.beneficiaryAddress(mailToSend), {
                        ...mailToSend,
                        isNewsLetter: context === 'newsletter'
                    }, idsList)
                }
            })),
            switchMap(({NombreContacte}) => this.checkNonContactableBeneficiaries({mail, idsList}).pipe(
                map<BeneficiaireAvecMailsModel[], SharedMailNonContactableBeneficiariesDialogData>(nonContactableBeneficiariesList => {
                    return {
                        context,
                        countContactedBeneficiaries: NombreContacte,
                        nonContactableBeneficiariesList
                    };
                }))
            ),
            tap(() => this.loading = false),
            switchMap(e => this.openBeneficiariesDialog(e)),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    private generateSelectedDocuments$(mail: SharedMail, generatedDocumentsList: GeneratedMailDocument[] = []): Observable<FichierCompletModel[]> {
        const selectedDocumentsList = generatedDocumentsList
            .filter((_, index) => !!mail.generatedDocumentsList?.[index]?.selected);

        if (!arrayNotEmpty(selectedDocumentsList)) {
            return of([]);
        }

        return forkJoin(selectedDocumentsList.map(document => document.generate$()));
    }

    sendUserPublipostageMail$({mail, beneficiaryId, idTypePubli, destinationMail}: PublipostageMail & {destinationMail: string}): Observable<string> {
        this.loading = true;

        return this.getMailModelPublipostage$({mail, beneficiaryId, idTypePubli}).pipe(
            switchMap(mailModelPublipostage => {
                const attachmentList = mailModelPublipostage?.ListePJ?.FichierModel || [];

                return this.graphqlService.mutateOne<'EnvoieMailUtilisateurPublipostage', MutationEnvoieMailUtilisateurPublipostageArgs>({
                    mutation: gql`
                        mutation EnvoieMailUtilisateurPublipostage($contexte: ContextInfosInput, $modeleEmail: ModelesModelEmailPieceJointeModelInput, $listePieceJointe: ArrayOfDocumentBaseModelInput, $id: Float) {
                            EnvoieMailUtilisateurPublipostage(
                                contexte: $contexte
                                modeleEmail: $modeleEmail
                                listePieceJointe: $listePieceJointe
                                id: $id
                            )
                        }
                    `,
                    variables: {
                        modeleEmail: {
                            ...mailModelPublipostage,
                            DestinataireModelEmail: destinationMail,
                            ListePJ: {
                                FichierModel: attachmentList
                            }
                        },
                        id: beneficiaryId
                    }
                });
            }),
            tap(() => {
                this.loading = false;
                this.snackbarService.open({title: 'L\'e-Mail simulé a bien été envoyé.'});
            }),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    openMailPreviewDialog$({mail, beneficiaryId, idTypePubli}: PublipostageMail): Observable<ModelesModelEmailPieceJointeModel> {
        this.loading = true;

        return this.getMailModelPublipostage$({mail, beneficiaryId, idTypePubli}).pipe(
            tap(mailWithPublipostage => {
                this.loading = false;

                const dialogConfig = new KalDialogConfig<SharedMailPreviewDialogData>({
                    title: 'Simuler e-Mail envoyé',
                    hasBackdrop: true,
                    backdropClass: 'cdk-overlay-dark-backdrop',
                    data: {
                        mail: {
                            ...mail,
                            content: mailWithPublipostage?.CorpsModelEmail || null
                        },
                        beneficiaryId
                    }
                });

                this.dialogService.open(SharedMailPreviewDialogComponent, dialogConfig);
            }),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    private getMailModelPublipostage$({mail, beneficiaryId, idTypePubli}: PublipostageMail): Observable<ModelesModelEmailPieceJointeModel> {
        return this.graphqlService.mutateOne<'GetModeleEMailPublipostage', MutationGetModeleEMailPublipostageArgs>({
            mutation: gql`
                mutation GetModeleEMailPublipostage(
                    $contexte: ContextInfosInput,
                    $rawXML: String
                ) {
                    GetModeleEMailPublipostage(
                        contexte: $contexte,
                        rawXML: $rawXML
                    ) {
                        CorpsModelEmail
                        DestinataireModelEmail
                        EmetteurModelEmail
                        IdModelEmail
                        IdModeleNewsLetter
                        IsIdentifiants
                        IsNewsLetter
                        IsPubliPostage
                        LibelleModelEmail
                        ObjectModelEmail
                        ListePJ {
                            FichierModel {
                                IdFile
                                Filename
                                Extension
                                LibelleLien
                                Token
                                Url
                            }
                        }
                    }
                }
            `,
            variables: {
                rawXML: generateModeleEMailPublipostageXML(this.getPublipostageMail(mail), beneficiaryId, idTypePubli)
            }
        });
    }

    private getMail({mail, mailParameters}: { mail: SharedMail, mailParameters: MailCeToBenef }): string {
        const {reportCode, parameters, benefId, mailCC} = mailParameters;
        const {to, subject, content, fromMail} = mail;
        const formattedMail = {
            IdBenef: benefId,
            Subject: subject,
            CopieCE: mailCC,
            Body: content,
            AdresseLesDeux: to === 'both',
            AdressePerso: to === 'personal',
            AdressePro: to === 'professional',
            AdressePrivilegier: true,
            AdrPrivilegierPro: true,
            IsHTML: true,
            From: fromMail,
            ReplyTo: this.store.selectSnapshot(UserState.currentDelegation).CEEmail,
            Identifiant: {
                GarderLoginAct: true,
                GarderMDPActuel: true
            },
            ListDestinataire: {DestinataireMailModelDTO: []},
            ListFichier: {FichierModel: []}
        };

        return generateSendMailWithCourrierXML(reportCode, formattedMail, parameters);
    }

    private getPublipostageMail(mail: SharedMail): ModelesModelEmailPieceJointeModelInput {
        const {
            content,
            subject,
            reportsDocumentsList,
            vouchersList,
            fromMail,
            themeIdList,
            isNewsLetter
        } = mail;
        const FichierModel = arrayNotEmpty(vouchersList) ? (vouchersList as FichierModelInput[]).map(e => ({
            IdFile: e.IdFile,
            LibelleLien: e.LibelleLien,
            Filename: e.Filename + '.' + e.Extension,
            ...(e.FileBytes ? {FileBytes: e.FileBytes} : {})
        })) : [];

        return {
            EmetteurModelEmail: fromMail,
            ObjectModelEmail: subject,
            IsNewsLetter: isNewsLetter,
            ListeIdReportJoint: {
                decimal: (reportsDocumentsList || []).filter(({selected}) => selected).map(({id}) => id)
            },
            CorpsModelEmail: content,
            ListePJ: {
                FichierModel
            },
            ListeIdTheme: {
                decimal: themeIdList
            }
        };
    }

    private beneficiaryAddress(mail: SharedMail): BeneficiaryAddress {
        const {to, privilegedAddress} = mail;
        const privilegedEmail = (property: string): boolean => (to === 'privileged' && privilegedAddress === property);
        const email = (property: string): boolean => privilegedEmail(property) || [property, 'both'].includes(to);

        return {
            IsEmailPro: email('professional'),
            IsEmailPerso: email('personal'),
            IsEmailProfOrPerso: to === 'both',
            IsEmailPriviligie: to === 'privileged',
            IsEmailPriviligiePerso: privilegedEmail('personal'),
            IsEmailPriviligiePro: privilegedEmail('professional')
        };
    }


    getMailModelDTORecipientFromMail(mail: SharedMail): MailModelDTORecipient{
        const {IsEmailPerso , IsEmailPriviligie , IsEmailPriviligiePerso , IsEmailPriviligiePro , IsEmailPro, IsEmailProfOrPerso} = this.beneficiaryAddress(mail);

        return {
            AdresseLesDeux: IsEmailProfOrPerso,
            AdressePerso: IsEmailPerso,
            AdressePrivilegier: IsEmailPriviligie,
            AdressePro: IsEmailPro,
            AdrPrivilegierPerso: IsEmailPriviligiePerso,
            AdrPrivilegierPro: IsEmailPriviligiePro
        }
    }

    private openBeneficiariesDialog(parameters: SharedMailNonContactableBeneficiariesDialogData): Observable<SharedMailNonContactableBeneficiariesDialogData> {
        const dialogConfig = new KalDialogConfig({
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            disableClose: true,
            data: parameters
        });

        return this.dialogService.open(SharedMailNonContactableBeneficiariesDialogComponent, dialogConfig).afterClosed;
    }

    private checkNonContactableBeneficiaries({mail, idsList}: { mail: SharedMail, idsList: number[], context?: MailContext }): Observable<BeneficiaireAvecMailsModel[]> {
        const {publipostageTypeId} = mail;
        const beneficiaryAddress = this.beneficiaryAddress(mail);

        return this.graphqlService.mutateOne<'GetBenefNonContactable', MutationGetBenefNonContactableArgs>({
            mutation: gql`
                mutation GetBenefNonContactable($contexte: ContextInfosInput, $crit: CriteresBenefContactModelInput, $idTypePublipostage: Int, $listeId: ArrayOfdecimalInput) {
                    GetBenefNonContactable(contexte: $contexte, crit: $crit, idTypePublipostage: $idTypePublipostage, listeId: $listeId) {
                        BeneficiaireAvecMailsModel {
                          Id
                          Nom
                          Prenom
                          Matricule
                          MailPerso
                          MailPro
                        }
                    }
                }`,
            variables: {
                crit: {
                    HaveEmailPerso: !beneficiaryAddress.IsEmailPerso ? null : false,
                    HaveEmailProfessionel: !beneficiaryAddress.IsEmailPro ? null : false,
                    HaveEmailProfOrPerso: !beneficiaryAddress.IsEmailProfOrPerso ? null : false,
                    HaveEmailPriviligie: !beneficiaryAddress.IsEmailPriviligie ? null : false
                },
                idTypePublipostage: publipostageTypeId,
                listeId: {
                    decimal: idsList
                }
            },
        }).pipe(
            map(GetBenefNonContactable => GetBenefNonContactable?.BeneficiaireAvecMailsModel || [])
        );
    }

    private getPublipostageMailsLimits(): Observable<any> {
        if (this.blockingLimit !== null || this.warningLimit !== null) {
            return of(null);
        }

        type mutations =
            'GetValueParametrePublipostageLimiteAvertissement'
            | 'GetValueParametrePublipostageLimiteBlocage';
        type types =
            MutationGetValueParametrePublipostageLimiteAvertissementArgs
            | MutationGetValueParametrePublipostageLimiteBlocageArgs;

        return this.graphqlService.mutateMultiple<mutations, types>({
            mutation: gql`
                mutation GetLimitePublipostageMail($contexte: ContextInfosInput) {
                    GetValueParametrePublipostageLimiteAvertissement(contexte: $contexte)
                    GetValueParametrePublipostageLimiteBlocage(contexte: $contexte)
                }`,
        }).pipe(
            tap(({GetValueParametrePublipostageLimiteBlocage, GetValueParametrePublipostageLimiteAvertissement}) => {
                this.blockingLimit = GetValueParametrePublipostageLimiteBlocage;
                this.warningLimit = GetValueParametrePublipostageLimiteAvertissement;
            }),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    private checkPublipostageMailsLimits({mail, idsList}: { mail: SharedMail, idsList: number[] }): Observable<any> {
        const countMailToSend = idsList.length;
        const timeEnvois = (countMailToSend * 1.5 * ((mail.reportsDocumentsList || []).length || 1)) / 60;
        let canValid = true;

        const dialogConfig = new KalDialogConfig<ConfirmDialogData>({
            title: 'Limite de l\'e-Mail',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            disableClose: true,
            data: {
                submitLabel: 'Continuer',
            }
        });

        if (countMailToSend > this.blockingLimit) {
            dialogConfig.data = {
                ...dialogConfig.data,
                body: `Vous êtes sur le point de contacter ${countMailToSend} bénéficiaire,
                    ce qui dépasse la limite de ${this.blockingLimit} autorisée par le module publipostage.
                    Merci d'affiner vos critères de génération avant d'effectuer l'envoi.`,
                cancelLabel: 'Fermer'
            };
            canValid = false;

        } else if (countMailToSend >= this.warningLimit) {
            dialogConfig.data = {
                ...dialogConfig.data,
                body: `Vous êtes sur le point de contacter ${countMailToSend} bénéficiaires.
                    Le temps de génération et d'envoi des e-Mails prendra environ ${timeEnvois} minutes.
                    Vous pouvez consulter votre historique de publipostage pour connaître le statut de votre envoi.`,
                cancelLabel: 'Annuler'
            };
        } else {
            return of(null);
        }

        return this.dialogService.open(ConfirmDialogComponent, dialogConfig).afterClosed.pipe(
            filter(e => canValid && !!e?.accepted)
        );
    }
}
